import {
	Button,
	Card,
	Col,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Modal,
	Row,
	Select,
	Tag,
} from "antd";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
	getMyResignation,
	submitResignation,
} from "../../redux/rtk/features/resignation/resignationSlice";
import { loadSingleStaff } from "../../redux/rtk/features/user/userSlice";
import getUserFromToken from "../../utils/getUserFromToken";
import dayjs from "dayjs";

const { TextArea } = Input;

const RESIGNATION_REASONS = [
	{ value: "better_opportunity", label: "Better opportunity" },
	{ value: "personal", label: "Personal" },
	{ value: "relocation", label: "Relocation" },
	{ value: "retirement", label: "Retirement" },
	{ value: "health", label: "Health" },
	{ value: "other", label: "Other" },
];

// 1 = approve, 2 = reject, 0 = pending. Approved only when both HR and Manager approved; else Pending (or Rejected if either rejected).
const getStatusTag = (record) => {
	const managerApproval = record?.ManagerApproval ?? record?.managerApproval;
	const hrApproval = record?.HRApproval ?? record?.hrApproval;
	const m = typeof managerApproval === "number" ? managerApproval : (record?.ManagerApprovalStatus ?? record?.managerApprovalStatus);
	const h = typeof hrApproval === "number" ? hrApproval : (record?.HRApprovalStatus ?? record?.hrApprovalStatus);
	const managerRejected = m === 2 || String(m).toUpperCase() === "REJECTED" || String(m) === "2";
	const hrRejected = h === 2 || String(h).toUpperCase() === "REJECTED" || String(h) === "2";
	const bothApproved = (m === 1 || String(m).toUpperCase() === "APPROVED") && (h === 1 || String(h).toUpperCase() === "APPROVED");

	if (managerRejected || hrRejected) return <Tag color="red">Rejected</Tag>;
	if (bothApproved) return <Tag color="green">Approved</Tag>;
	return <Tag color="orange">Pending</Tag>;
};

export default function Resignation() {
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const [confirmVisible, setConfirmVisible] = useState(false);
	const [confirmPayload, setConfirmPayload] = useState(null);

	const loginId = getUserFromToken();
	const isLogged = Boolean(localStorage.getItem("isLogged"));
	const { myResignation, loading } = useSelector((state) => state.resignation);
	const currentEmployee = useSelector(
		(state) => state.users?.user?.EmployeesDetail?.[0] ?? null
	);
	const reportingToId = currentEmployee?.ReportingToId ?? null;

	useEffect(() => {
		if (loginId) {
			dispatch(getMyResignation(loginId));
		}
	}, [dispatch, loginId]);

	useEffect(() => {
		if (loginId) {
			dispatch(loadSingleStaff(loginId));
		}
	}, [dispatch, loginId]);

	const handleFinish = (values) => {
		const payload = {
			EmployeeId: parseInt(loginId, 10),
			ManagerId: reportingToId != null ? Number(reportingToId) : null,
			NoticePeriodStartDate: values.noticePeriodStartDate
				? dayjs(values.noticePeriodStartDate).format("YYYY-MM-DD")
				: dayjs().format("YYYY-MM-DD"),
			ResignationReason: values.reason,
			NoticePeriod: values.noticePeriodDays ?? undefined,
			Remarks: values.remarks ?? undefined,
		};
		setConfirmPayload(payload);
		setConfirmVisible(true);
	};

	const handleConfirmSubmit = () => {
		if (!confirmPayload) return;
		dispatch(submitResignation(confirmPayload))
			.unwrap()
			.then(() => {
				setConfirmVisible(false);
				setConfirmPayload(null);
				form.resetFields();
				dispatch(getMyResignation(loginId));
			})
			.catch(() => {});
	};

	if (!isLogged) {
		return <Navigate to="/admin/auth/login" replace />;
	}

	const lastWorkingDate = myResignation?.lastWorkingDate || myResignation?.LastWorkingDay || myResignation?.LastWorkingDate;
	const resignationReason = myResignation?.ResignationReason ?? myResignation?.resignationReason ?? myResignation?.reason ?? myResignation?.Reason ?? myResignation?.resignationReasonName ?? "";
	const noticePeriodStartDate = myResignation?.NoticePeriodStartDate ?? myResignation?.noticePeriodStartDate;
	const submittedDate = myResignation?.submittedAt || myResignation?.SubmittedAt || myResignation?.createdAt || myResignation?.CreatedAt;
	const rejectionReason = myResignation?.rejectionReason || myResignation?.RejectionReason;
	const managerApproval = myResignation?.ManagerApproval ?? myResignation?.managerApproval;
	const hrApproval = myResignation?.HRApproval ?? myResignation?.hrApproval;
	const isRejected = managerApproval === 2 || hrApproval === 2;
	const isBothApproved = managerApproval === 1 && hrApproval === 1;
	const hasPendingOrActive = myResignation && !isRejected && !isBothApproved;

	return (
		<>
			<Row gutter={[24, 24]}>
				<Col xs={24} lg={12}>
					<Card title="Submit resignation">
						{hasPendingOrActive ? (
							<p className="text-gray-600">
								You have a pending resignation request. You cannot submit another until it is
								processed.
							</p>
						) : (
							<Form
								form={form}
								layout="vertical"
								onFinish={handleFinish}
								initialValues={{
									noticePeriodDays: 60,
									noticePeriodStartDate: dayjs(),
								}}
							>
								<Form.Item
									name="noticePeriodStartDate"
									label="Notice period start date"
									rules={[{ required: true, message: "Please select notice period start date" }]}
								>
									<DatePicker
										format="YYYY-MM-DD"
										style={{ width: "100%" }}
										disabledDate={(current) => current && current > dayjs().endOf("day")}
									/>
								</Form.Item>
								<Form.Item
									name="reason"
									label="Resignation reason"
									rules={[{ required: true, message: "Please select a reason" }]}
								>
									<Select
										placeholder="Select reason"
										options={RESIGNATION_REASONS}
										allowClear
									/>
								</Form.Item>
								<Form.Item name="noticePeriodDays" label="Notice period (days)">
									<InputNumber min={0} style={{ width: "100%" }} />
								</Form.Item>
								<Form.Item name="remarks" label="Remarks">
									<TextArea rows={3} placeholder="Optional additional details" />
								</Form.Item>
								<Form.Item>
									<Button type="primary" htmlType="submit" loading={loading} block>
										Submit resignation
									</Button>
								</Form.Item>
							</Form>
						)}
					</Card>
				</Col>

				<Col xs={24} lg={12}>
					<Card title="My resignation">
						{!myResignation ? (
							<p className="text-gray-600">
								You have not submitted any resignation request.
							</p>
						) : (
							<div className="space-y-2">
								<p>
									<strong>Status:</strong> {getStatusTag(myResignation)}
								</p>
								{lastWorkingDate && (
									<p>
										<strong>Last working date:</strong>{" "}
										{dayjs(lastWorkingDate).format("DD MMM YYYY")}
									</p>
								)}
								{resignationReason && (
									<p>
										<strong>Resignation reason:</strong>{" "}
										{RESIGNATION_REASONS.find((r) => r.value === resignationReason)?.label || resignationReason}
									</p>
								)}
								{noticePeriodStartDate && (
									<p>
										<strong>Notice period start date:</strong>{" "}
										{dayjs(noticePeriodStartDate).format("DD MMM YYYY")}
									</p>
								)}
								{submittedDate && (
									<p>
										<strong>Submitted on:</strong>{" "}
										{dayjs(submittedDate).format("DD MMM YYYY HH:mm")}
									</p>
								)}
								{rejectionReason && (
									<p>
										<strong>Rejection reason:</strong> {rejectionReason}
									</p>
								)}
							</div>
						)}
					</Card>
				</Col>
			</Row>

			<Modal
				title="Confirm resignation"
				open={confirmVisible}
				onOk={handleConfirmSubmit}
				onCancel={() => {
					setConfirmVisible(false);
					setConfirmPayload(null);
				}}
				okText="Submit"
				confirmLoading={loading}
			>
				{confirmPayload && (
					<p>
						You are about to submit your resignation.
						{confirmPayload.NoticePeriodStartDate && (
							<>
								{" "}
								Notice period start:{" "}
								<strong>
									{dayjs(confirmPayload.NoticePeriodStartDate).format("DD MMM YYYY")}
								</strong>
								.
							</>
						)}
						{" "}
						Do you want to continue?
					</p>
				)}
			</Modal>
		</>
	);
}
